/**
 * LlmTaskInspector — Phase 5a-4, second per-type editor in V3 (forked from V4).
 *
 * Edits LLM / AI Inference task config. Field set follows V3's
 * llm_task configSchema (soarNodes.js:611) — Mode, Provider, Model,
 * System Prompt, User Prompt Template. Temperature / maxTokens /
 * outputFormat / RAG fields land in a 5a-4b follow-up if/when needed.
 *
 * Spread-preservation contract identical to HttpTaskInspector — every
 * mutation routes through onUpdate(id, { config: { ...cfg, [key]: val }})
 * so V3-only config fields survive the save round-trip.
 */

import React from 'react';

const MODES = ['chat', 'text'];
const PROVIDERS = ['anthropic', 'openai', 'azure_openai', 'bedrock', 'gemini'];

// Models grouped by provider. The editor shows ALL of them for now;
// Phase 6 polish could filter by selected provider.
const MODELS = [
  // Anthropic
  'claude-sonnet-4-6', 'claude-opus-4-6', 'claude-haiku-4-5-20251001',
  // OpenAI
  'gpt-4o', 'gpt-4-turbo', 'gpt-4', 'gpt-3.5-turbo',
  // Google
  'gemini-1.5-pro', 'gemini-1.5-flash',
];

export default function LlmTaskInspector({ node, onUpdate, readOnly = false }) {
  const cfg = node.config || {};
  const mode = cfg.mode || 'chat';
  const llmProvider = cfg.llmProvider || 'anthropic';
  const model = cfg.model || 'claude-sonnet-4-6';
  const systemPrompt = cfg.systemPrompt || '';
  const userPromptTemplate = cfg.userPromptTemplate || '';

  const setField = (key, value) => {
    if (readOnly || typeof onUpdate !== 'function') return;
    onUpdate(node.id, { config: { ...cfg, [key]: value } });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <FieldGroup label="Mode" hint="chat = multi-turn; text = single prompt → completion">
        <select
          value={MODES.includes(mode) ? mode : 'chat'}
          onChange={(e) => setField('mode', e.target.value)}
          disabled={readOnly}
          style={selectStyle}
        >
          {MODES.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </FieldGroup>

      <FieldGroup label="Provider" hint="Must have a configured connector for this tenant.">
        <select
          value={PROVIDERS.includes(llmProvider) ? llmProvider : 'anthropic'}
          onChange={(e) => setField('llmProvider', e.target.value)}
          disabled={readOnly}
          style={selectStyle}
        >
          {PROVIDERS.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </FieldGroup>

      <FieldGroup label="Model">
        <select
          value={MODELS.includes(model) ? model : model}
          onChange={(e) => setField('model', e.target.value)}
          disabled={readOnly}
          style={selectStyle}
        >
          {MODELS.map((m) => <option key={m} value={m}>{m}</option>)}
          {!MODELS.includes(model) && model && (
            <option value={model}>{model} (custom)</option>
          )}
        </select>
      </FieldGroup>

      {mode === 'chat' && (
        <FieldGroup label="System Prompt" hint="Defines model behavior. Chat mode only.">
          <textarea
            value={systemPrompt}
            onChange={(e) => setField('systemPrompt', e.target.value)}
            placeholder="You are a security analyst..."
            disabled={readOnly}
            rows={4}
            style={textareaStyle}
          />
        </FieldGroup>
      )}

      <FieldGroup label="User Prompt Template" hint="Use {{variable}} placeholders for runtime substitution.">
        <textarea
          value={userPromptTemplate}
          onChange={(e) => setField('userPromptTemplate', e.target.value)}
          placeholder="Analyze this alert: {{alert}} for {{threatType}}"
          disabled={readOnly}
          rows={6}
          style={textareaStyle}
        />
      </FieldGroup>

      <p style={{ fontSize: 10, color: '#94a3b8', margin: 0, lineHeight: 1.5 }}>
        Temperature, max tokens, output format, and RAG context bindings are
        available in V3 today. Open in V3 for advanced config; the basics
        edited here round-trip cleanly.
      </p>
    </div>
  );
}

/* ───── shared field UI (duplicated for now; Phase 6 will extract) ──── */

function FieldGroup({ label, hint, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: '#0f172a' }}>{label}</span>
      {children}
      {hint && (
        <span style={{ fontSize: 10, color: '#64748b' }}>{hint}</span>
      )}
    </label>
  );
}

const inputStyle = {
  width: '100%',
  padding: '6px 8px',
  fontSize: 12,
  color: '#0f172a',
  background: '#ffffff',
  border: '0.5px solid #cbd5e1',
  borderRadius: 4,
  outline: 'none',
  fontFamily: 'inherit',
};
const selectStyle = { ...inputStyle, appearance: 'auto' };
const textareaStyle = { ...inputStyle, fontFamily: 'SFMono-Regular, Menlo, monospace', fontSize: 11, lineHeight: 1.4 };
